import { EnvModeSwitch } from '@/components/Display/EnvModeSwitch'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { type EnvModeSelection, toModeParam } from '@/utils/envMode'
import { ClearOutlined, FileTextOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, Vertical } from '@gravitate-js/excalibrr'
import { Tabs } from 'antd'
import type { Dayjs } from 'dayjs'
import { useMemo, useState } from 'react'
import { DashboardRangePicker } from './DashboardRangePicker'
import { OrdersDashboard } from './dashboards/OrdersDashboard'
import { useOrderDashboardFilters } from './dashboards/OrdersDashboard/useOrderDashboardFilters'
import { lastDays, toDashboardWindow } from './dateRange'

const tabItems = [
  { key: 'orders', label: 'Orders', icon: <FileTextOutlined /> },
]

/**
 * The tab bar gets a surface of its own, only as wide as its tabs -- one
 * spanning the page would read as a page header rather than as something to
 * click. The range picker brings its own border, so it needs no surface
 * behind it.
 */
const TAB_BOX_CLASS = 'rounded-sm border border-[var(--gray-300)] px-3'
const TAB_BOX_STYLE = { background: 'var(--bg-2)' }

export function DashboardsPage() {
  const [activeTab, setActiveTab] = useState('orders')
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => lastDays(3))
  const reportWindow = useMemo(() => toDashboardWindow(range), [range])
  // Beside the range picker rather than inside the filters, for the reason the
  // window is not a filter either: there is always a selection, and clearing it
  // would leave nothing to report on. Defaults to Prod, like the other panels.
  const [mode, setMode] = useState<EnvModeSelection>('Prod')
  // Up here so the header can clear filters that are set several levels down,
  // across two panes of the dashboard.
  const orderFilters = useOrderDashboardFilters()

  return (
    <ErrorBoundary>
      <Vertical style={{ height: '100%' }}>
        <Horizontal gap={8} alignItems='stretch' justifyContent='space-between'>
          <div className={TAB_BOX_CLASS} style={TAB_BOX_STYLE}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              style={{ marginBottom: 0 }}
              tabBarStyle={{ marginBottom: 0, border: 'none' }}
              items={tabItems}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Absent rather than disabled with nothing to clear: it would
                otherwise read as a control the page always offers. `theme1` is
                excalibrr's info-coloured button -- the Crocus Purple the rest
                of the app accents with -- and brings its own hover states. */}
            {orderFilters.active && (
              <GraviButton
                theme1
                icon={<ClearOutlined />}
                onClick={orderFilters.clear}
              >
                Clear Filters
              </GraviButton>
            )}
            <EnvModeSwitch value={mode} onChange={setMode} />
            <DashboardRangePicker value={range} onChange={setRange} />
          </div>
        </Horizontal>
        <Vertical className='pt-2' style={{ flex: 1, minHeight: 0 }}>
          {activeTab === 'orders' && (
            <OrdersDashboard
              reportWindow={reportWindow}
              mode={toModeParam(mode)}
              filters={orderFilters}
            />
          )}
        </Vertical>
      </Vertical>
    </ErrorBoundary>
  )
}
